import { useState, type FormEvent } from "react";
import emailjs from "@emailjs/browser";
import Layout from "@/components/feature/Layout";
import SectionHeader from "@/components/base/SectionHeader";

const needOptions = [
  "Website",
  "E-commerce",
  "Custom Software",
  "Automation",
  "AI Solution",
  "Digital Transformation",
  "Not Sure",
];

const budgetOptions = [
  "Under ₦250,000",
  "₦250,000 – ₦500,000",
  "₦500,000 – ₦1,000,000",
  "₦1,000,000+",
  "Not sure",
];


export default function Contact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
  e.preventDefault();

  const form = e.currentTarget;
  const formData = new FormData(form);

  // Honeypot check
  const hp = (formData.get("company_alt") as string)?.trim();

  if (hp) {
    setStatus("success");
    setErrorMsg("");
    form.reset();
    return;
  }

  try {
    await emailjs.send(
      "service_ki58zfr",
      "template_qhsuvjh",
      {
        name: formData.get("name"),
        company: formData.get("company"),
        email: formData.get("email"),
        phone: formData.get("phone"),
        need: formData.get("need"),
        message: formData.get("message"),
        budget: formData.get("budget"),
        timeline: formData.get("timeline"),
      },
      {
        publicKey: "lg3Kk4NTbfghAV7si",
      }
    );

    setStatus("success");
    setErrorMsg("");
    form.reset();

  } catch (error) {
    console.error(error);

    setStatus("error");
    setErrorMsg(
      "Unable to send your inquiry. Please try again or email us directly."
    );
  }
};

  return (
    <Layout>
      <div className="pt-16 md:pt-[72px]">
        {/* Header */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-100">
          <div className="mx-auto max-w-[1280px]">
            <SectionHeader
              eyebrow="Contact"
              title="Let's Build Something Useful."
              subtitle="Tell us about your project and what you're trying to achieve. We'll help you figure out the right solution."
            />
          </div>
        </section>

        {/* Form + contact info */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20">
          <div className="mx-auto max-w-[1280px] grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Contact info */}
            <div className="lg:col-span-1 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground-950">
                  Get in touch directly
                </h3>
                <div className="mt-5 space-y-4">
                  <a
                    href="mailto:info@emchontechnologies.com"
                    className="flex items-center gap-3 text-foreground-700 hover:text-primary-600 transition-colors"
                  >
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <span className="text-sm">info@emchontechnologies.com</span>
                  </a>
                  <a
                    href="https://wa.me/2348033830563"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 text-foreground-700 hover:text-primary-600 transition-colors"
                  >
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className="ri-whatsapp-line text-lg"></i>
                    </span>
                    <span className="text-sm">Chat on WhatsApp</span>
                  </a>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground-950">
                  What happens next?
                </h3>
                <ol className="mt-4 space-y-3">
                  {[
                    "We review your inquiry",
                    "We reply within 1–2 business days",
                    "We schedule a call to understand your needs",
                    "We propose the right solution",
                  ].map((step, i) => (
                    <li key={step} className="flex items-start gap-3 text-sm text-foreground-600">
                      <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950 text-xs font-semibold">
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-800">
                      Name <span className="text-primary-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your full name"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800">
                      Company / Organization
                    </label>
                    <input
                      type="text"
                      name="company"
                      placeholder="Your company or organization"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800">
                      Email <span className="text-primary-600">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@company.com"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800">
                      Phone / WhatsApp
                    </label>
                    <input
                      type="text"
                      name="phone"
                      placeholder="+234 ..."
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="block text-sm font-medium text-foreground-800">
                    What do you need?
                  </label>
                  <select
                    name="need"
                    className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                  >
                    {needOptions.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-5">
                  <label className="block text-sm font-medium text-foreground-800">
                    Tell us about your project
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Describe what you're trying to achieve, the challenge you're facing, or the solution you have in mind..."
                    className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 resize-y"
                  />
                  <p className="mt-1 text-xs text-foreground-400">500 characters max</p>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-800">
                      Estimated budget
                    </label>
                    <select
                      name="budget"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    >
                      {budgetOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800">
                      Desired timeline
                    </label>
                    <input
                      type="text"
                      name="timeline"
                      placeholder="e.g. As soon as possible, 2–3 months"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                {/* Anti-spam honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <button
                  type="submit"
                  className="mt-8 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-8 py-3.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 transition-all duration-200 cursor-pointer"
                >
                  Send Project Inquiry <i className="ri-send-plane-line text-base"></i>
                </button>

                {status === "success" && (
                  <div className="mt-6 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                    Thank you! Your inquiry has been received. We'll get back to
                    you shortly.
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-6 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                    {errorMsg}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}